import React, { useEffect, useRef, useState } from "react";
import { useCart } from "../../context/CartContext";
import { MAX_QTY, formatMoney } from "../../lib/shopify";
import { buildItem, trackBeginCheckout, trackRemoveFromCart, trackViewCart } from "../../lib/tracking";

const FOCUSABLE =
  'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

const QtyButton = ({ label, onClick, disabled, children }) => (
  <button
    type="button"
    aria-label={label}
    onClick={onClick}
    disabled={disabled}
    className="flex h-[36px] w-[36px] items-center justify-center rounded-[8px] border border-[#C3C3C3] text-[18px] leading-none text-[#474747] disabled:opacity-40"
  >
    {children}
  </button>
);

const CartDrawer = () => {
  const { cart, isOpen, isLoading, error, closeCart, updateLine, removeLine } = useCart();
  const panelRef = useRef(null);
  const [entered, setEntered] = useState(false);

  // slide-in transition
  useEffect(() => {
    if (!isOpen) {
      setEntered(false);
      return undefined;
    }
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [isOpen]);

  // scroll lock, initial focus, focus restore, Esc + focus trap
  useEffect(() => {
    if (!isOpen) return undefined;
    const previouslyFocused = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panelRef.current?.focus();

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        closeCart();
        return;
      }
      if (e.key !== "Tab" || !panelRef.current) return;
      const items = panelRef.current.querySelectorAll(FOCUSABLE);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === panelRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
      previouslyFocused?.focus?.();
    };
  }, [isOpen, closeCart]);

  // view_cart fires once per opening, when the cart has items
  const hasLines = (cart?.lines?.length || 0) > 0;
  useEffect(() => {
    if (!isOpen || !hasLines) return;
    trackViewCart(
      cart.lines.map((l) =>
        buildItem({ id: l.merchandise?.id, title: "Концентрирано дете", price: l.merchandise?.price, quantity: l.quantity }),
      ),
      cart.cost?.totalAmount?.currencyCode,
    );
  }, [isOpen, hasLines]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const lines = cart?.lines || [];

  const handleRemove = (line) => {
    trackRemoveFromCart(
      [buildItem({ id: line.merchandise?.id, title: "Концентрирано дете", price: line.merchandise?.price, quantity: line.quantity })],
      line.cost?.totalAmount?.currencyCode,
    );
    removeLine(line.id).catch(() => {});
  };

  const handleCheckout = () => {
    const items = lines.map((l) =>
      buildItem({
        id: l.merchandise?.id,
        title: "Концентрирано дете",
        price: l.merchandise?.price,
        quantity: l.quantity,
      }),
    );
    trackBeginCheckout(items, cart?.cost?.totalAmount?.currencyCode);
  };

  return (
    <div className="fixed inset-0 z-[200]">
      <div
        className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${entered ? "opacity-100" : "opacity-0"}`}
        onClick={closeCart}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cart-drawer-title"
        tabIndex={-1}
        className={`absolute right-0 top-0 flex h-full w-full flex-col bg-white outline-none transition-transform duration-300 sm:max-w-[420px] ${
          entered ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between border-b border-[#E8E0D6] px-[20px] py-[16px]">
          <h2 id="cart-drawer-title" className="font-[sofia-sans-700] text-[24px] leading-[120%] text-[#353535]">
            Количка
          </h2>
          <button
            type="button"
            onClick={closeCart}
            aria-label="Затвори количката"
            className="flex h-[40px] w-[40px] items-center justify-center text-[#474747]"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M3 3l14 14M17 3L3 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-[20px] py-[16px]">
          {lines.length === 0 ? (
            <p className="font-[nunito-sans-400] text-[16px] leading-[150%] text-[#474747]">
              Количката е празна.
            </p>
          ) : (
            <ul className="flex flex-col gap-[20px]">
              {lines.map((line) => {
                const m = line.merchandise;
                return (
                  <li key={line.id} className="flex gap-[14px]">
                    {m?.image?.url && (
                      <img
                        src={m.image.url}
                        alt={m.image.altText || m.product?.title || ""}
                        width="80"
                        height="80"
                        className="h-[80px] w-[80px] shrink-0 rounded-[8px] object-cover"
                      />
                    )}
                    <div className="flex flex-1 flex-col gap-[8px]">
                      <p className="font-[sofia-sans-700] text-[18px] leading-[120%] text-[#353535]">
                        {m?.product?.title}
                      </p>
                      <p className="font-[nunito-sans-400] text-[14px] text-[#474747]">
                        {formatMoney(m?.price)}
                      </p>
                      <div className="flex items-center gap-[8px]">
                        <QtyButton
                          label="Намали количеството"
                          disabled={isLoading}
                          onClick={() => updateLine(line.id, line.quantity - 1).catch(() => {})}
                        >
                          −
                        </QtyButton>
                        <span
                          aria-live="polite"
                          className="min-w-[24px] text-center font-[nunito-sans-600] text-[16px]"
                        >
                          {line.quantity}
                        </span>
                        <QtyButton
                          label="Увеличи количеството"
                          disabled={isLoading || line.quantity >= MAX_QTY}
                          onClick={() => updateLine(line.id, line.quantity + 1).catch(() => {})}
                        >
                          +
                        </QtyButton>
                        <button
                          type="button"
                          onClick={() => handleRemove(line)}
                          disabled={isLoading}
                          className="ml-auto font-[nunito-sans-400] text-[14px] text-[#6B6B6B] underline"
                        >
                          Премахни
                        </button>
                      </div>
                    </div>
                    <p className="font-[sofia-sans-700] text-[18px] text-[#353535]">
                      {formatMoney(line.cost?.totalAmount)}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
          {error && (
            <p role="alert" className="mt-[16px] font-[nunito-sans-400] text-[14px] text-[#B3261E]">
              Нещо се обърка. Опитай отново.
            </p>
          )}
        </div>

        {lines.length > 0 && (
          <div className="flex flex-col gap-[12px] border-t border-[#E8E0D6] px-[20px] py-[16px]">
            <div className="flex items-center justify-between font-[sofia-sans-700] text-[20px] text-[#353535]">
              <span>Общо</span>
              <span>{formatMoney(cart?.cost?.subtotalAmount)}</span>
            </div>
            <a
              href={cart?.checkoutUrl}
              onClick={handleCheckout}
              className="flex items-center justify-center rounded-[12px] bg-[#E9531E] px-6 py-4 font-[sofia-sans-800] text-[18px] leading-[120%] text-white shadow-[0_8px_20px_rgba(233,83,30,0.4)] hover:bg-[#D2440F]"
            >
              Към плащане
            </a>
            <button
              type="button"
              onClick={closeCart}
              className="font-[nunito-sans-400] text-[16px] text-[#474747] underline"
            >
              Продължи пазаруването
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartDrawer;
